/**
 * What the link a visit arrived on said about itself.
 *
 * A shared result points at its own grid and carries the sharer's best beside
 * it (GDD §6.3), which is what lets the first screen name what sent the player
 * here instead of opening on nothing. Pure and parameterised on the query
 * string, so the boot path is testable without a location.
 *
 * Nothing here is trusted and nothing is stored. The grid number decides only
 * what the first screen *says* — never which grid is dealt, which is always
 * today's — and the megawatt figure is the sharer's word typed into a link,
 * so it reaches no streak, no tier and no field. Both are refused rather than
 * clamped: a number outside the range the game can produce is not a reading
 * that needs rescuing, it is a link somebody edited.
 */
export interface Arrival {
  /** The grid the link named, or `null` if it named none. */
  day: number | null;
  /** The megawatts claimed on that grid, or `null`. */
  mw: number | null;
}

/**
 * Grid numbers run from 1 and climb by one a day forever, so the ceiling only
 * has to be past any date a player could plausibly be on. Beyond it the link
 * is malformed rather than far in the future.
 */
const MAX_GRID = 100_000;

/**
 * Megawatts are a percentage of the reference and the scale is open at the
 * top (`refMWFor`), but only just — a reading beyond this is not a grid
 * anybody played.
 */
const MAX_MW = 200;

export function parseArrival(search: string): Arrival {
  let q: URLSearchParams;
  try {
    q = new URLSearchParams(search);
  } catch {
    return { day: null, mw: null };
  }

  const rawDay = Number(q.get('d'));
  const day =
    q.has('d') && Number.isInteger(rawDay) && rawDay >= 1 && rawDay <= MAX_GRID ? rawDay : null;

  const rawMW = Number(q.get('mw'));
  const mw =
    day !== null && q.has('mw') && Number.isFinite(rawMW) && rawMW > 0 && rawMW <= MAX_MW
      ? Math.round(rawMW * 10) / 10
      : null;

  return { day, mw };
}
